/**
 * Investigation Factors Component
 * Admin-only section for managing case characteristics that affect matching.
 *
 * Features:
 *   • Auto-suggested factors from server-side keyword scan with badges
 *     so investigators can see *which* selections came from the
 *     scanner vs were manually applied.
 *   • Live "current search radius" indicator that updates as factors
 *     are toggled — visible whether the panel is collapsed or open.
 *   • Per-factor matched-keyword tooltips on auto-suggested badges.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import {
  Settings2, Plane, ChevronDown,
  Loader2, Save, HelpCircle, Navigation, Truck, Shield,
  Tent, Globe, Wheat, AlertCircle, Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Lifestyle factors that affect geo-matching. Factor IDs MUST match
// the server's `services/investigation_factor_suggester.KEYWORD_RULES`
// keys 1:1 so the auto-suggest pipeline can light up the same boxes.
const LIFESTYLE_FACTORS = [
  {
    id: 'high_mobility',
    label: 'High Mobility / Frequent Traveler',
    icon: Plane,
    emoji: '✈️',
    effect: 'Search radius: 300 → 1,000+ miles',
    description: 'Frequently traveled for work or personal reasons',
  },
  {
    id: 'transient',
    label: 'Transient Lifestyle / Homeless',
    icon: Tent,
    emoji: '🏕️',
    effect: 'Reduces geographic penalties significantly',
    description: 'No fixed address, moved frequently between locations',
  },
  {
    id: 'long_distance_driver',
    label: 'Long-Distance Driver / Trucking',
    icon: Truck,
    emoji: '🚛',
    effect: 'Enables highway corridor matching',
    description: 'Professional driver or frequent long-distance traveler',
  },
  {
    id: 'military_travel',
    label: 'Military / Government Travel',
    icon: Shield,
    emoji: '🪖',
    effect: 'Includes military base proximity matching',
    description: 'Military service member or government employee with frequent relocations',
  },
  {
    id: 'border_crosser',
    label: 'Possible Border Crossing',
    icon: Globe,
    emoji: '🌎',
    effect: 'Enables international matching',
    description: 'May have crossed international borders (US-Mexico, US-Canada)',
  },
  {
    id: 'seasonal_worker',
    label: 'Seasonal Worker / Migrant Worker',
    icon: Wheat,
    emoji: '🌾',
    effect: 'Matches seasonal work locations',
    description: 'Followed seasonal work patterns (agriculture, tourism, etc.)',
  },
];

const WHEN_TO_USE_EXAMPLES = [
  'Truck drivers',
  'Traveling salespeople',
  'Backpackers and hitchhikers',
  'People experiencing homelessness',
  'Military service members',
  'Agricultural workers',
  'Individuals known to cross borders',
];

export function InvestigationFlags({ caseId, caseType, isAdmin }) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mobilityData, setMobilityData] = useState(null);

  // Form state — multiple factors can be selected, each tagged with a
  // source ('manual' | 'auto_suggested') so the UI can render a badge.
  const [selectedFactors, setSelectedFactors] = useState([]);
  const [factorSources, setFactorSources] = useState({});
  const [suggestedFactors, setSuggestedFactors] = useState({}); // {id: [matched keywords]}
  const [notes, setNotes] = useState('');

  // Fetch current mobility data + server-side suggestions.
  const fetchMobilityData = useCallback(async () => {
    if (!caseId || !caseType) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/mobility/case/${caseType}/${caseId}`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      const mobility = data.mobility || {};
      const suggested = data.suggested_factors || {};
      setMobilityData(mobility);
      setSuggestedFactors(suggested);

      // Reconcile selection. Order of preference:
      //   1. Stored `factors` array (manual + prior auto-suggested)
      //   2. Legacy `mobility_type` mapping
      //   3. NEW server suggestions (only if nothing was stored at all)
      let factors = [];
      let sources = { ...(mobility.factor_sources || {}) };

      if (Array.isArray(mobility.factors) && mobility.factors.length > 0) {
        factors = [...mobility.factors];
      } else if (mobility.mobility_flag) {
        if (mobility.mobility_type === 'border_crosser') factors.push('border_crosser');
        else if (mobility.mobility_type === 'transient') factors.push('transient');
        else if (mobility.mobility_type === 'international_travel') factors.push('border_crosser');
        else if (mobility.mobility_type === 'trafficking_risk') factors.push('high_mobility');
        else factors.push('high_mobility');
      }

      // For any case that has NEVER been annotated, light up the
      // server suggestions so the panel shows them pre-checked.
      const isVirgin =
        !mobility.admin_override
        && (!Array.isArray(mobility.factors) || mobility.factors.length === 0);
      if (isVirgin && Object.keys(suggested).length > 0) {
        for (const fid of Object.keys(suggested)) {
          if (!factors.includes(fid)) factors.push(fid);
          if (!sources[fid]) sources[fid] = 'auto_suggested';
        }
      }

      // Backfill any source we don't yet know about.
      for (const fid of factors) {
        if (!sources[fid]) {
          sources[fid] = suggested[fid] ? 'auto_suggested' : 'manual';
        }
      }

      setSelectedFactors(factors);
      setFactorSources(sources);
      setNotes(mobility.mobility_notes || '');
    } catch (error) {
      // Silent fail — panel stays empty rather than blocking the page.
       
      console.error('Failed to fetch mobility data:', error);
    } finally {
      setLoading(false);
    }
  }, [caseId, caseType]);

  // Fetch immediately so the search-radius header (visible even when
  // the panel is collapsed) reflects real data.
  useEffect(() => {
    fetchMobilityData();
  }, [fetchMobilityData]);

  // Toggle a factor — flips selection state AND promotes the source
  // to 'manual' (the investigator just touched it).
  const toggleFactor = (factorId) => {
    setSelectedFactors((prev) => {
      const next = prev.includes(factorId)
        ? prev.filter((f) => f !== factorId)
        : [...prev, factorId];
      // Update sources map alongside the selection change so the
      // tooltip / badge reflect the correct provenance immediately.
      setFactorSources((prevSrc) => {
        const out = { ...prevSrc };
        if (next.includes(factorId)) {
          // Investigator explicitly checked the box → mark as manual,
          // EVEN if the scanner had auto-suggested it. Once a human
          // vouches, we stop labelling it as a tentative pick.
          out[factorId] = 'manual';
        } else {
          delete out[factorId];
        }
        return out;
      });
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const mobilityFlag = selectedFactors.length > 0;
      let mobilityType = null;
      if (selectedFactors.includes('border_crosser')) mobilityType = 'border_crosser';
      else if (selectedFactors.includes('transient')) mobilityType = 'transient';
      else if (selectedFactors.includes('high_mobility')) mobilityType = 'international_travel';
      else if (mobilityFlag) mobilityType = 'transient';

      const res = await fetch(`${API}/api/mobility/case/${caseType}/${caseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          mobility_flag: mobilityFlag,
          mobility_type: mobilityType,
          mobility_confidence:
            selectedFactors.length >= 2 ? 'high'
              : selectedFactors.length === 1 ? 'medium'
                : null,
          mobility_notes: notes || null,
          factors: selectedFactors,
          factor_sources: factorSources,
        }),
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to update investigation factors');
      }
      const data = await res.json();
      setMobilityData(data.mobility);
      // Server may have updated factor_sources (e.g. backfilling
      // omitted entries) — sync local state so the next render is
      // byte-identical to persisted state.
      if (data.mobility?.factor_sources) {
        setFactorSources(data.mobility.factor_sources);
      }
      toast.success('Investigation factors saved! Matching will now reflect these conditions.', {
        duration: 3000,
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) return null;

  const hasNearBorder = mobilityData?.near_border;
  const borderDistance = mobilityData?.border_distance_miles;
  const hasFactors = selectedFactors.length > 0;
  // Currently-effective search radius — driven by the LIVE selection
  // state so the header updates the moment a checkbox is toggled.
  const currentRadius = hasFactors ? '1,000+' : '300';
  // How many of the active picks are "auto_suggested" vs touched by a human.
  const autoCount = selectedFactors.filter(
    (fid) => factorSources[fid] === 'auto_suggested',
  ).length;

  return (
    <TooltipProvider>
      <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mt-0">
        <Card
          data-testid="investigation-factors-card"
          className={`transition-all duration-300 ${
            hasFactors
              ? 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20'
              : 'border-slate-200 dark:border-slate-700'
          }`}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors py-3">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2 flex-wrap">
                  <Settings2 className="w-4 h-4 text-amber-600" />
                  Investigation Factors
                  {hasFactors && (
                    <Badge
                      data-testid="investigation-factors-active-count"
                      className="bg-amber-500 text-white text-xs ml-1">
                      {selectedFactors.length} active
                    </Badge>
                  )}
                  {autoCount > 0 && (
                    <Badge
                      data-testid="investigation-factors-auto-count"
                      variant="outline"
                      className="text-[10px] border-sky-400 text-sky-700 dark:text-sky-300 dark:border-sky-600">
                      <Sparkles className="w-3 h-3 mr-1" />
                      {autoCount} auto-suggested
                    </Badge>
                  )}
                  {hasNearBorder && !hasFactors && (
                    <Badge variant="outline" className="text-xs border-amber-400 text-amber-600">
                      Near Border
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-3">
                  {/* Always-visible live search radius — visible even
                      when the panel is collapsed so investigators see
                      the impact of their picks at a glance. */}
                  <div
                    data-testid="search-radius-header"
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-all duration-300 ${
                      hasFactors
                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                    }`}>
                    <Navigation className="w-3.5 h-3.5" />
                    <span className="text-[11px] uppercase tracking-wide opacity-70">Radius</span>
                    <span
                      data-testid="search-radius-value"
                      className="text-sm font-bold tabular-nums">
                      {currentRadius} mi
                    </span>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full"
                        onClick={(e) => e.stopPropagation()}>
                        <HelpCircle className="w-4 h-4 text-slate-400" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs p-3">
                      <p className="font-semibold mb-2">When to use these factors:</p>
                      <ul className="text-xs space-y-1">
                        {WHEN_TO_USE_EXAMPLES.map((example, i) => (
                          <li key={i} className="flex items-center gap-1">
                            <span className="text-amber-500">•</span> {example}
                          </li>
                        ))}
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                  <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </CardHeader>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <CardContent className="pt-0 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    These factors adjust how the system searches for potential matches. Selections
                    take effect on the next match recompute.
                  </p>

                  {/* Search Radius Indicator — bigger / in-card variant */}
                  <div
                    data-testid="search-radius-pane"
                    className={`flex items-center justify-between p-3 rounded-lg transition-all duration-300 ${
                      hasFactors
                        ? 'bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-600'
                        : 'bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700'
                    }`}>
                    <div className="flex items-center gap-2">
                      <Navigation className={`w-4 h-4 ${hasFactors ? 'text-amber-600' : 'text-slate-500'}`} />
                      <span className="text-sm font-medium">Effective search radius</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-lg font-bold tabular-nums ${hasFactors ? 'text-amber-600' : 'text-slate-600 dark:text-slate-300'}`}>
                        {currentRadius} miles
                      </span>
                      {hasFactors && (
                        <Badge className="bg-amber-500 text-white text-xs">Expanded</Badge>
                      )}
                    </div>
                  </div>

                  {/* Border Proximity Auto-Detection */}
                  {borderDistance !== null && borderDistance !== undefined && (
                    <div className={`flex items-center gap-2 p-2 rounded text-xs ${
                      hasNearBorder
                        ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                        : 'bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400'
                    }`}>
                      <Sparkles className="w-3 h-3" />
                      <span>
                        Auto-detected: {borderDistance.toFixed(0)} miles from nearest border
                        {hasNearBorder && ' — Consider enabling "Border Crossing" factor'}
                      </span>
                    </div>
                  )}

                  {/* Lifestyle Factors Checkboxes */}
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Mobility & Lifestyle Factors
                    </Label>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                      Select all that apply. Boxes flagged
                      <Badge variant="outline" className="text-[10px] mx-1 border-sky-400 text-sky-700">
                        <Sparkles className="w-2.5 h-2.5 mr-0.5" />Auto-suggested
                      </Badge>
                      were inferred from case text — uncheck any that don&apos;t fit.
                    </p>

                    <div className="space-y-2">
                      {LIFESTYLE_FACTORS.map((factor) => {
                        const isSelected = selectedFactors.includes(factor.id);
                        const source = factorSources[factor.id];
                        const isAuto = isSelected && source === 'auto_suggested';
                        const matchedKeywords = suggestedFactors[factor.id] || [];

                        return (
                          <div
                            key={factor.id}
                            onClick={() => toggleFactor(factor.id)}
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                              isSelected
                                ? isAuto
                                  ? 'bg-sky-50 dark:bg-sky-900/20 border-sky-300 dark:border-sky-600 ring-1 ring-sky-200 dark:ring-sky-700'
                                  : 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-600 ring-1 ring-amber-200 dark:ring-amber-700'
                                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-amber-200 dark:hover:border-amber-700'
                            }`}
                            data-testid={`factor-${factor.id}`}>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleFactor(factor.id)}
                              className={isAuto
                                ? 'mt-0.5 data-[state=checked]:bg-sky-500 data-[state=checked]:border-sky-500'
                                : 'mt-0.5 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500'}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-base">{factor.emoji}</span>
                                <span className={`font-medium text-sm ${
                                  isSelected
                                    ? isAuto
                                      ? 'text-sky-700 dark:text-sky-300'
                                      : 'text-amber-700 dark:text-amber-400'
                                    : 'text-slate-700 dark:text-slate-300'
                                }`}>
                                  {factor.label}
                                </span>
                                {isAuto && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge
                                        data-testid={`factor-${factor.id}-auto-badge`}
                                        variant="outline"
                                        className="text-[10px] border-sky-400 text-sky-700 dark:text-sky-300 dark:border-sky-600 cursor-help"
                                        onClick={(e) => e.stopPropagation()}>
                                        <Sparkles className="w-2.5 h-2.5 mr-1" />
                                        Auto-suggested
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      <p className="font-semibold mb-1 text-xs">
                                        Inferred from case text
                                      </p>
                                      {matchedKeywords.length > 0 ? (
                                        <p className="text-[11px] leading-snug">
                                          Matched terms: {matchedKeywords.slice(0, 5).map((k) => `"${k}"`).join(', ')}
                                          {matchedKeywords.length > 5 ? '…' : ''}
                                        </p>
                                      ) : (
                                        <p className="text-[11px] leading-snug">
                                          Suggested by keyword scan. Uncheck if it doesn&apos;t fit this case.
                                        </p>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                {/* Show suggestion hint even when NOT
                                    selected — investigators see what
                                    the scanner flagged so they don't
                                    miss potentially-relevant signals. */}
                                {!isSelected && matchedKeywords.length > 0 && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge
                                        data-testid={`factor-${factor.id}-suggestion-hint`}
                                        variant="outline"
                                        className="text-[10px] border-dashed border-sky-300 text-sky-600 dark:text-sky-400 cursor-help"
                                        onClick={(e) => e.stopPropagation()}>
                                        <Sparkles className="w-2.5 h-2.5 mr-1" />
                                        scanner hit
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      <p className="text-[11px] leading-snug">
                                        Keyword scanner detected this in the case text
                                        ({matchedKeywords.slice(0, 3).map((k) => `"${k}"`).join(', ')}) — click to enable.
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                {factor.description}
                              </p>
                              <div className={`mt-1 text-xs font-medium ${
                                isSelected
                                  ? (isAuto
                                    ? 'text-sky-600 dark:text-sky-400'
                                    : 'text-amber-600 dark:text-amber-500')
                                  : 'text-slate-400'
                              }`}>
                                → {factor.effect}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* How This Changes Matching */}
                  {hasFactors && (
                    <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                      <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-400 mb-2 flex items-center gap-2">
                        <Settings2 className="w-4 h-4" />
                        How this changes matching
                      </h4>
                      <ul className="text-xs text-blue-600 dark:text-blue-400 space-y-1">
                        <li>• Search radius increases from 300 → 1,000+ miles</li>
                        {selectedFactors.includes('border_crosser') && (
                          <li>• Matches from other countries become more likely</li>
                        )}
                        {selectedFactors.includes('transient') && (
                          <li>• Geographic penalties are significantly reduced</li>
                        )}
                        {selectedFactors.includes('long_distance_driver') && (
                          <li>• Highway corridor matches are prioritized</li>
                        )}
                        <li>• Long-distance matches are given higher priority</li>
                      </ul>
                    </div>
                  )}

                  {hasFactors && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        <strong>Note:</strong> Expanding the search may increase the number of potential matches.
                        Review results carefully to filter out less relevant matches.
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="investigation-notes" className="text-sm">
                      Additional Notes
                    </Label>
                    <Textarea
                      id="investigation-notes"
                      placeholder="Add context about why these factors apply (e.g., 'Known to work seasonal agricultural jobs in California and Washington')..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className="text-sm"
                      data-testid="investigation-notes-input"
                    />
                  </div>

                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className={`w-full transition-all ${
                      hasFactors
                        ? 'bg-amber-600 hover:bg-amber-700'
                        : 'bg-slate-600 hover:bg-slate-700'
                    }`}
                    data-testid="save-factors-btn">
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Investigation Factors
                      </>
                    )}
                  </Button>
                </>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </TooltipProvider>
  );
}

export default InvestigationFlags;
